<script setup lang="ts">
import { AuctionState, type SaleAssetBusListingItem, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { NO_VALUE_PLACEHOLDER } from '~/constants/sale';
import { isAuction, isDirectSale, type PlatformSaleListingItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleListingItemEntity;
}

const { sale } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    navigate: [];
}>();

// COMPOSABLE
const { tt, formatUnits } = useTypedI18n();
const { auctionDetail, directSaleDetail } = useA11y();
const { isBundle } = useSaleType(() => sale.type);
const { getPlatformBusClassesLabel } = usePlatformBusClass();
const { formatFirstRegistrationCompact } = useAssetRegistration();
const { priceColor, statusDotColor, title } = useSaleStatus(toRef(() => sale));

// DATA
const hasCountdown = computed(() => isAuction(sale) && [AuctionState.Pending, AuctionState.Running].includes(sale.state));

const mainAsset = computed<SaleAssetBusListingItem>(() => sale.mainAsset);

const classLabel = computed<string | undefined>(() => {
    const parts = [
        isBundle.value && sale?.assetCount ? tt('asset.vehicleCount', { count: sale?.assetCount }) : null,
        mainAsset.value.class ? getPlatformBusClassesLabel(mainAsset.value.class) : null,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : undefined;
});

const isHighlighted = computed(() => isAuction(sale) && !sale.soldOffPlatform && (!sale.reservePriceSet || !!sale.reservePriceReached));

const linkProps = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { to: { name: ROUTE_NAMES.AUCTION_DETAIL, params: { slug: sale.slug } }, 'aria-label': auctionDetail(sale) };
        case SellingMethod.DirectSale:
            return { to: { name: ROUTE_NAMES.DIRECT_SALE_DETAIL, params: { slug: sale.slug } }, 'aria-label': directSaleDetail(sale) };
    }
});
</script>

<template>
    <NuxtLinkLocale v-bind="linkProps" @click="emit('navigate')">
        <SlpBaseCard :highlighted="isHighlighted">
            <template #header>
                <SlpCardImages :main-asset="mainAsset" />
                <div class="absolute top-3 right-3 left-3 flex justify-between">
                    <div class="flex items-start gap-3">
                        <SaleBadges :sale="sale" icon-only />
                    </div>
                    <SaleFavoritesButton :sale="sale" />
                </div>
                <AuctionReservePriceNotice v-if="isAuction(sale) && !sale.soldOffPlatform" class="absolute right-3 bottom-3" :auction="sale" />
                <DslpCardNegotiationAvailableBadge v-if="isDirectSale(sale)" class="absolute right-3 bottom-3" :direct-sale="sale" />
            </template>

            <template #selling-method><SaleSellingMethod :selling-method="sale.sellingMethod" size="xs" /></template>
            <template v-if="classLabel" #class>{{ classLabel }}</template>

            <template #model>{{ mainAsset.brand.brandValue }} {{ mainAsset.model.modelValue }}</template>

            <template #registration>{{ formatFirstRegistrationCompact(mainAsset) }}</template>

            <template #kilometer>{{ formatUnits.km(mainAsset.km) ?? NO_VALUE_PLACEHOLDER }}</template>

            <template #euro-norm>{{ mainAsset.euroNorm ? tt(`asset.euroNorm.${mainAsset.euroNorm}`) : '-' }}</template>

            <template #price>
                <SalePrice :sale="sale" :class="priceColor">
                    <AuctionMaxBidderIcon v-if="isAuction(sale)" :auction="sale" />
                </SalePrice>
            </template>

            <template #status>
                <AdpCountdown v-if="isAuction(sale) && hasCountdown" :auction="sale" />
                <template v-else>
                    <p class="flex items-start gap-1.5 text-xs leading-6 font-semibold tracking-wider uppercase">
                        <span class="mt-2 size-2 shrink-0 rounded-full" :class="statusDotColor" aria-hidden="true" />
                        {{ title }}
                    </p>
                </template>
            </template>
        </SlpBaseCard>
    </NuxtLinkLocale>
</template>
